// Alerts: the inbox, tracked opportunities, and alert settings (/alerts, /alerts/tracked, /alerts/settings).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { api } from "../api/client";
import type { Alert, BudgetSummary, NoticeItem } from "../api/types";
import { useUI } from "../app/context";
import { keys, useSession, useUnread } from "../app/queries";
import { Badge } from "../components/Badge";
import { GlobalSearch } from "../components/GlobalSearch";
import { DuePill } from "../components/Rows";
import { ALERT_KINDS, FREQUENCIES, dayLabel, human, timeOf } from "../lib/alerts";
import { dueText, lineRef, money } from "../lib/format";

type Tab = "inbox" | "tracked" | "settings";

export function Alerts() {
  const { tab: raw } = useParams();
  const tab: Tab = raw === "tracked" || raw === "settings" ? raw : "inbox";
  const unread = useUnread().data ?? 0;
  const tabs: [Tab, string, string][] = [
    ["inbox", "Inbox", "/alerts"],
    ["tracked", "Tracked", "/alerts/tracked"],
    ["settings", "Settings", "/alerts/settings"],
  ];
  return (
    <>
      <div className="top">
        <div className="date">Alerts</div>
        <GlobalSearch />
      </div>
      <h1>Alerts</h1>
      <div className="tabs" role="tablist">
        {tabs.map(([k, label, to]) => (
          <Link key={k} role="tab" to={to} aria-selected={tab === k}>
            {label}
            {k === "inbox" && unread ? <> <span className="count">{unread}</span></> : null}
          </Link>
        ))}
      </div>
      <div id="tab">
        {tab === "tracked" ? (
          <>
            <Tracked />
            <TrackedBudget />
          </>
        ) : tab === "settings" ? (
          <Settings />
        ) : (
          <Inbox />
        )}
      </div>
    </>
  );
}

const Loading = () => <p className="empty">Loading…</p>;

function Inbox() {
  const client = useQueryClient();
  const { openPanel } = useUI();
  const inbox = useQuery({ queryKey: ["alerts", "inbox"], queryFn: () => api<{ alerts: Alert[]; unread: number }>("/api/alerts") });
  const read = useMutation({
    mutationFn: (ids: number[] | null) => api("/api/alerts/read", { method: "POST", body: ids ? { ids } : {} }),
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: ["alerts"] }), client.invalidateQueries({ queryKey: keys.brief })]),
  });
  if (!inbox.data) return <Loading />;
  const { alerts, unread } = inbox.data;
  if (!alerts.length)
    return (
      <div className="empty-state">
        <h2>No alerts yet</h2>
        <p>Alerts appear here when:</p>
        <ul>
          <li>
            a feed or saved search you turned alerts on for gets new records (use <b>🔔 Alerts</b> on a feed, or <b>Alert me</b> on a search);
          </li>
          <li>
            an opportunity you track changes: its due date, text, attachments or status, or it's due within a week or a day. Marking something Pursue
            or Watch tracks it.
          </li>
        </ul>
      </div>
    );
  const open = (key: string) => openPanel({ kind: "notice", key, feedId: null });
  const openBudget = (id: string) => openPanel({ kind: "budget", id });
  let last = "";
  return (
    <>
      <div className="inbox-bar">
        <span>{unread ? `${unread} unread` : "All caught up"}</span>
        {unread ? (
          <button className="btn" id="read-all" onClick={() => read.mutate(null)}>
            Mark all read
          </button>
        ) : null}
      </div>
      {alerts.map((a) => {
        const label = dayLabel(a.created_at);
        const header = label !== last ? <div className="day-label">{label}</div> : null;
        last = label;
        const [kindLabel, tone] = ALERT_KINDS[a.kind] ?? [human(a.kind), "info"];
        const link = a.target_kind === "feed" ? `/feeds/${a.target_id}` : a.target_kind === "search" ? `/saved/${a.target_id}` : "";
        return (
          <div key={a.id}>
            {header}
            <article
              className={`alert ${a.read_at ? "" : "unread"}`}
              data-alert={a.id}
              onClick={() => {
                if (!a.read_at && !read.isPending) read.mutate([a.id]);
              }}
            >
              <span className={`a-dot ${tone}`} aria-hidden="true" />
              <div className="a-main">
                <div className="a-kind">
                  {kindLabel} · {timeOf(a.created_at)}
                  {a.read_at ? null : <span className="sr"> (unread)</span>}
                </div>
                <h3>
                  {a.notice ? (
                    <button className="a-link" data-open={a.notice.key} onClick={() => open(a.notice!.key)}>
                      {a.title}
                    </button>
                  ) : a.target_kind === "budget" && a.keys[0] ? (
                    <button className="a-link" data-open-budget={a.keys[0]} onClick={() => openBudget(a.keys[0]!)}>
                      {a.title}
                    </button>
                  ) : link ? (
                    <Link to={link} className="a-link">
                      {a.title}
                    </Link>
                  ) : (
                    a.title
                  )}
                </h3>
                {a.body && (!a.items.length || !link) ? <p>{a.body}</p> : null}
                {a.target_kind === "budget" && a.notice && a.keys[0] ? (
                  <button className="text-btn" onClick={() => openBudget(a.keys[0]!)}>
                    Open the budget line
                  </button>
                ) : null}
                {a.items.length && link ? (
                  <>
                    <ul className="a-items">
                      {a.items.map((n) => (
                        <li key={n.key}>
                          <button className="a-link" data-open={n.key} onClick={() => open(n.key)}>
                            {n.title}
                          </button>{" "}
                          <span className="muted-s">
                            {n.buyer} · {dueText(n)}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {a.keys.length > a.items.length && link ? (
                      <Link to={link} className="more-link">
                        See all {a.keys.length}
                      </Link>
                    ) : null}
                  </>
                ) : null}
              </div>
              {a.notice ? (
                <div>
                  <DuePill n={a.notice} />
                </div>
              ) : null}
            </article>
          </div>
        );
      })}
    </>
  );
}

type TrackedItem = NoticeItem & { tracked: true; origin: string; tracked_at: string };

function Tracked() {
  const client = useQueryClient();
  const { openPanel, showToast } = useUI();
  const tracked = useQuery({ queryKey: ["tracked"], queryFn: () => api<{ items: TrackedItem[] }>("/api/tracked") });
  const untrack = useMutation({
    mutationFn: (key: string) => api("/api/tracked/" + encodeURIComponent(key), { method: "DELETE" }),
    onSuccess: () => {
      showToast("Stopped tracking.");
      return client.invalidateQueries({ queryKey: ["tracked"] });
    },
  });
  if (!tracked.data) return <Loading />;
  const items = tracked.data.items;
  if (!items.length)
    return (
      <div className="empty-state">
        <h2>You're not tracking anything</h2>
        <p>
          Open any opportunity and choose <b>🔔 Track</b>, or mark it Pursue or Watch in a feed. You'll get an alert when its due date, text,
          attachments or status change, and a week and a day before it's due.
        </p>
      </div>
    );
  const todayIso = new Date().toISOString().slice(0, 10);
  const open = items.filter((n) => !n.deadline || n.deadline >= todayIso);
  const past = items.filter((n) => n.deadline && n.deadline < todayIso);
  const row = (n: TrackedItem) => (
    <div
      key={n.key}
      className="row"
      data-key={n.key}
      role="button"
      tabIndex={0}
      onClick={() => openPanel({ kind: "notice", key: n.key, feedId: null })}
      onKeyDown={(e) => {
        if (e.key === "Enter") openPanel({ kind: "notice", key: n.key, feedId: null });
      }}
    >
      <Badge agency={n.agency} small />
      <div style={{ minWidth: 0 }}>
        <div className="t">{n.title}</div>
        <div className="m">
          {n.buyer}
          {" "}
          {n.kind}
          {" "}
          {n.origin === "mark" ? "From Pursue or Watch" : "Tracked"}{" "}
          {new Date(n.tracked_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
        </div>
      </div>
      <div>
        <DuePill n={n} />
      </div>
      <div className="fitcol">
        <button
          className="btn small"
          data-untrack={n.key}
          onClick={(e) => {
            e.stopPropagation();
            untrack.mutate(n.key);
          }}
        >
          Stop tracking
        </button>
      </div>
    </div>
  );
  return (
    <>
      <p className="muted-s">{items.length} tracked. Sorted by due date.</p>
      <div className="rows">{open.map(row)}</div>
      {past.length ? (
        <>
          <div className="day-label">Past due</div>
          <div className="rows">{past.map(row)}</div>
        </>
      ) : null}
    </>
  );
}

interface AlertSettings {
  email: boolean;
  email_available: boolean;
  address: string;
  feeds: { id: number; name: string; frequency: string; following: boolean; mine: boolean }[];
  searches: { id: number; name: string; query: string; frequency: string }[];
}

function Settings() {
  const me = useSession();
  const client = useQueryClient();
  const { showToast } = useUI();
  const settings = useQuery({ queryKey: ["alerts", "settings"], queryFn: () => api<AlertSettings>("/api/alerts/settings") });
  const refresh = () => Promise.all([client.invalidateQueries({ queryKey: ["alerts", "settings"] }), client.invalidateQueries({ queryKey: keys.searches })]);
  if (!settings.data) return <Loading />;
  const d = settings.data;
  const setEmail = async (on: boolean) => {
    await api("/api/alerts/settings", { method: "PUT", body: { email: on } });
    showToast(on ? "Alert email on." : "Alert email off. Alerts still appear here.");
    await refresh();
  };
  const setFrequency = async (kind: "feed" | "search", id: number, frequency: string) => {
    await api(kind === "feed" ? `/api/feeds/${id}/alerts` : `/api/searches/${id}`, { method: kind === "feed" ? "PUT" : "PATCH", body: { frequency } });
    showToast("Alert schedule saved.");
    await refresh();
  };
  const select = (kind: "feed" | "search", id: number, value: string, label: string) => (
    <select data-kind={kind} data-id={id} aria-label={`Alerts for ${label}`} value={value} onChange={(e) => setFrequency(kind, id, e.target.value)}>
      {FREQUENCIES.map(([v, l]) => (
        <option key={v} value={v}>
          {v === "off" ? "Off" : l}
        </option>
      ))}
    </select>
  );
  return (
    <div className="settings">
      <section>
        <h2>Email</h2>
        {d.email_available ? (
          <label className="check">
            <input type="checkbox" id="pref-email" checked={d.email} onChange={(e) => setEmail(e.target.checked)} />
            <span>
              Email my alerts to <b>{d.address}</b>
              <small>Feeds and searches follow the schedule you choose below. Tracked opportunities email as they change.</small>
            </span>
          </label>
        ) : (
          <p className="muted-s">
            Email isn't set up on this server yet, so alerts appear here in GovChew only.{" "}
            {me.superuser ? (
              <>
                Set <code>GOVCHEW_SMTP_HOST</code> and <code>GOVCHEW_MAIL_FROM</code> to turn it on.
              </>
            ) : (
              "Ask your GovChew administrator to set it up."
            )}
          </p>
        )}
      </section>
      <section>
        <h2>Feeds</h2>
        <p className="muted-s">Get an alert when new records match a feed.</p>
        <table className="plain-table">
          <tbody>
            {d.feeds.length ? (
              d.feeds.map((f) => (
                <tr key={f.id}>
                  <td>
                    <Link to={`/feeds/${f.id}`}>{f.name}</Link>
                    {f.mine ? null : f.following ? <span className="muted-s"> following</span> : null}
                  </td>
                  <td>{select("feed", f.id, f.frequency, f.name)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="muted-s">No feeds yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      <section>
        <h2>Saved searches</h2>
        <p className="muted-s">Get an alert when new records match a saved search.</p>
        <table className="plain-table">
          <tbody>
            {d.searches.length ? (
              d.searches.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/saved/${s.id}`}>{s.name}</Link> <code className="muted-s">{s.query}</code>
                  </td>
                  <td>{select("search", s.id, s.frequency, s.name)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="muted-s">No saved searches. Run a search and choose Save search.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      <section>
        <h2>Tracked opportunities</h2>
        <p className="muted-s">
          Always on for what you track. You hear about due-date changes, amendments, new attachments, closing, and reminders a week and a day before
          the due date. <Link to="/alerts/tracked">Manage tracked opportunities</Link>
        </p>
      </section>
    </div>
  );
}

interface TrackedLine extends BudgetSummary {
  gone?: boolean;
  tracking: { threshold_pct: number; min_amount: number; created_at: string };
}

/** Budget lines being tracked, with the threshold each alerts at. */
function TrackedBudget() {
  const client = useQueryClient();
  const { openPanel } = useUI();
  const q = useQuery({ queryKey: ["budget", "tracked"], queryFn: () => api<{ items: TrackedLine[] }>("/api/budget/tracked") });
  const items = q.data?.items ?? [];
  if (!items.length) return null;
  const stop = async (id: string) => {
    await api("/api/budget/tracked/" + encodeURIComponent(id), { method: "DELETE" });
    client.invalidateQueries({ queryKey: ["budget"] });
  };
  return (
    <section className="tracked-budget">
      <div className="day-label">Budget lines</div>
      <div className="rows">
        {items.map((b) => (
          <div key={b.id} className="row" role="button" tabIndex={0} onClick={() => !b.gone && openPanel({ kind: "budget", id: b.id })}>
            <Badge agency={(b.agency ?? "") + " " + (b.service ?? "")} small />
            <div style={{ minWidth: 0 }}>
              <div className="t">{b.title}</div>
              <div className="m">
                {b.gone ? "No longer in the budget" : `${b.kind === "cj" ? lineRef(b) : b.service + " · " + lineRef(b)} · ${money(b.request)} FY${String(b.edition).slice(2)}`}
                {"\u2002"}
                {thresholdText(b.tracking.threshold_pct, b.tracking.min_amount)}
              </div>
            </div>
            <div />
            <div className="fitcol">
              <button
                className="btn small"
                onClick={(e) => {
                  e.stopPropagation();
                  stop(b.id);
                }}
              >
                Stop tracking
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function thresholdText(pct: number, amount: number): string {
  const base = pct ? `Alerts on changes of ${pct}% or more` : "Alerts on any change";
  return base + (amount ? ` and at least ${money(amount)}` : "");
}
